// components/Pagination.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import PremiumSelect from './PremiumSelect';

const Pagination = ({
  currentPage,
  totalPages,
  totalCount,
  currentCount,
  onPageChange,
  onPageSizeChange = null,
  pageSize = null,
  pageSizeOptions = [10, 25, 50],
  showPageSize = false,
  hasActiveFilters = false,
  isSeeAll = false,
  className = "",
  fixed = false,         // still supported if you need a global footer later
  forceVisible = false,  // show even when only 1 page
}) => {
  const { t } = useTranslation();
  if (!forceVisible && !showPageSize && totalPages <= 1) return null;

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 3) {
      endPage = Math.min(5, totalPages);
    }
    if (currentPage > totalPages - 3) {
      startPage = Math.max(totalPages - 4, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => onPageChange(i)}
          className={`h-9 w-9 flex items-center justify-center rounded-md transition-colors text-sm font-medium
            ${i === currentPage
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-700 hover:bg-gray-100'
            }`}
        >
          {i}
        </button>
      );
    }

    return pages;
  };

  const wrapperClasses = `w-full ${className}`;

  return (
    <div className={wrapperClasses} aria-label="pagination">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 px-6 py-3">
        <div className="text-gray-700 text-sm font-medium">
          {t('pagination.showing')} <span className="font-semibold">{currentCount}</span> {t('pagination.of')} <span className="font-semibold">{totalCount}</span> {t('pagination.results')}
          {hasActiveFilters && (
            <span className="ml-2 text-blue-600">
              {t('pagination.filteredResults')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {showPageSize && onPageSizeChange && pageSize && (
            <div className="flex items-center gap-2 mr-3 sm:mr-4">
              <span className="text-gray-600 text-xs sm:text-sm font-medium whitespace-nowrap">
                {t('pagination.show')}:
              </span>
              <PremiumSelect
                value={pageSize}
                onChange={(val) => onPageSizeChange(Number(val))}
                options={pageSizeOptions}
                size="sm"
                dropUp={true}
                searchable={false}
                className="w-28 sm:w-32"
              />
            </div>
          )}

          {!isSeeAll && totalPages > 1 && (
            <>
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-gray-700 text-sm font-medium"
              >
                {t('pagination.previous')}
              </button>

              <div className="flex items-center gap-1">
                {renderPageNumbers()}
              </div>

              <button
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-gray-700 text-sm font-medium"
              >
                {t('pagination.next')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Pagination;